import { useState } from 'react';
import { useCourtSources } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader, type SortDir } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { formatNumber, timeAgo } from '@/lib/utils';
import { Database, Scale } from 'lucide-react';

const statusVariant = (status: string): 'success' | 'info' | 'warning' | 'destructive' | 'secondary' => {
  const map: Record<string, 'success' | 'info' | 'warning' | 'destructive' | 'secondary'> = {
    active: 'success', failed: 'destructive', pending: 'warning', disabled: 'secondary',
  };
  return map[status] ?? 'secondary';
};

const platformVariant = (p: string | null) => {
  const map: Record<string, string> = {
    courtlistener: 'indigo',
    pacer: 'teal',
    state_portal: 'purple',
    recap: 'orange',
    seed_data: 'secondary',
  };
  return (map[p ?? ''] ?? 'secondary') as never;
};

const jurisdictionVariant = (j: string) => {
  const map: Record<string, string> = {
    federal: 'sky',
    state: 'violet',
  };
  return (map[j] ?? 'secondary') as never;
};

export default function CourtSourcesPage() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('court_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
      setPage(1);
    }
  };

  const { data, isLoading } = useCourtSources({
    page,
    sort: sortField,
    order: sortDir,
  });

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="banner-chip">
              <h1 className="text-2xl font-bold banner-title">Court Data Sources</h1>
            </div>
            {data && (
              <div className="banner-chip">
                <Badge variant="outline" className="text-xs">{data.total} total</Badge>
              </div>
            )}
          </div>
        </div>
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-700 dark:text-amber-400">Sources</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Loading court sources..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="Court" field="court_name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>Jurisdiction</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>
                      <SortableHeader label="Type" field="source_type" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Platform" field="platform" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Status" field="status" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Records" field="record_count" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Last Crawled" field="last_crawled_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.sources.map((s, idx) => (
                    <TableRow
                      key={s.id}
                      className={idx % 2 === 0 ? '' : 'bg-table-row-even'}
                      style={{
                        borderLeft: s.status === 'active' ? '3px solid #34d399'
                          : s.status === 'failed' ? '3px solid #f87171'
                          : s.status === 'pending' ? '3px solid #fbbf24'
                          : '3px solid transparent',
                      }}
                    >
                      <TableCell className="font-medium max-w-[250px] truncate" title={s.court_name}>
                        {s.court_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant={jurisdictionVariant(s.jurisdiction)} className="text-xs capitalize">
                          {s.jurisdiction}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs capitalize">{s.court_level}</TableCell>
                      <TableCell className="font-mono text-xs">{s.source_type}</TableCell>
                      <TableCell>
                        <Badge variant={platformVariant(s.platform)} className="font-mono text-xs">
                          {s.platform || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(s.status)} className="text-xs capitalize">{s.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(s.record_count)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{timeAgo(s.last_crawled_at)}</TableCell>
                    </TableRow>
                  ))}
                  {data?.sources.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Scale className="h-10 w-10 text-muted-foreground/40" />
                          <p className="text-muted-foreground">No court sources configured yet</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePager
                page={page}
                onPageChange={setPage}
                total={data?.total}
                pageSize={data?.limit ?? 20}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
